<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useQuery } from '@tanstack/vue-query'
import { Car, ClipboardList, CreditCard, FileText, LayoutGrid, LogOut, Package, Plus, Search, UserCircle, Users, UsersRound } from 'lucide-vue-next'
import { CkButton, CkModal, CkSidebarNav, CkThemeToggle, CkUserCard } from '@ck/ui'
import { accountsApi } from '@ck/api-client'
import { hasRole, logout } from '@ck/auth'
import { useThemeStore } from '../stores/theme'
import NovaOsForm from './NovaOsForm.vue'
import logoUrl from '../../design-system/logo-ck.png'

const route = useRoute()
const router = useRouter()
const themeStore = useThemeStore()
themeStore.apply()

const canManageTeam = computed(() => hasRole('admin') || hasRole('manager'))
const account = useQuery({ queryKey: ['accounts', 'me'], queryFn: () => accountsApi.me() })

const items = computed(() => [
  { label: 'Painel', icon: LayoutGrid, to: '/', matchNames: ['home'] },
  { label: 'Veículos', icon: Car, to: '/veiculos', matchNames: ['veiculos'] },
  { label: 'Ordens de serviço', icon: ClipboardList, to: '/ordens', matchNames: ['ordens', 'ordem-detalhe'] },
  { label: 'Clientes', icon: UsersRound, to: '/clientes', matchNames: ['clientes', 'cliente-detalhe'] },
  ...(canManageTeam.value ? [{ label: 'Pagamentos', icon: CreditCard, to: '/pagamentos', matchNames: ['pagamentos'] }] : []),
  ...(canManageTeam.value ? [{ label: 'Estoque', icon: Package, to: '/estoque', matchNames: ['estoque'] }] : []),
  ...(canManageTeam.value ? [{ label: 'Equipe', icon: Users, to: '/equipe', matchNames: ['equipe'] }] : []),
  ...(canManageTeam.value ? [{ label: 'Relatórios', icon: FileText, to: '/relatorios', matchNames: ['relatorios'] }] : []),
  { label: 'Minha conta', icon: UserCircle, to: '/minha-conta', matchNames: ['minha-conta'] }
])

const search = ref(typeof route.query.q === 'string' ? route.query.q : '')
watch(() => route.query.q, (q) => { search.value = typeof q === 'string' ? q : '' })
let debounce: ReturnType<typeof setTimeout> | undefined
function onSearchInput() {
  clearTimeout(debounce)
  debounce = setTimeout(() => {
    router.replace({ query: { ...route.query, q: search.value.trim() || undefined } })
  }, 300)
}

const showNovaOs = ref(false)
function onCreated(workOrderId: string) {
  showNovaOs.value = false
  router.push({ name: 'ordem-detalhe', params: { id: workOrderId } })
}
</script>

<template>
  <div class="app-shell">
    <CkSidebarNav :items="items" :logo-src="logoUrl">
      <template #footer>
        <RouterLink to="/minha-conta" class="account-link" :class="{ active: route.name === 'minha-conta' }">
          <CkUserCard :name="account.data.value?.name || 'Gestor'" role="Gestor" />
        </RouterLink>
        <button type="button" class="logout" @click="logout"><LogOut :size="16"/>Sair</button>
      </template>
    </CkSidebarNav>
    <div class="main">
      <header class="topbar">
        <div class="search-box">
          <Search :size="17"/>
          <label class="sr-only" for="topbar-search">Buscar OS, placa, cliente</label>
          <input id="topbar-search" v-model="search" placeholder="Buscar OS, placa, cliente…" @input="onSearchInput">
        </div>
        <CkThemeToggle :dark="themeStore.theme === 'dark'" @toggle="themeStore.toggle"/>
        <CkButton v-if="canManageTeam" @click="showNovaOs = true"><Plus :size="17"/>Nova OS</CkButton>
      </header>
      <main class="content"><slot /></main>
    </div>
    <CkModal v-if="showNovaOs" title="Nova ordem de serviço" @close="showNovaOs = false">
      <NovaOsForm @created="onCreated"/>
    </CkModal>
  </div>
</template>

<style scoped>
.app-shell { display: flex; min-height: 100vh; background: var(--color-bg); color: var(--color-text); }
.main { flex: 1; display: flex; flex-direction: column; min-width: 0; }
.topbar { display: flex; align-items: center; gap: 16px; height: 72px; padding: 0 28px; border-bottom: 1px solid var(--color-border); flex-shrink: 0; }
.search-box { flex: 1; max-width: 340px; display: flex; align-items: center; gap: 9px; padding: 9px 13px; border: 1px solid var(--color-border); border-radius: var(--radius-md); background: var(--color-surface); color: var(--color-text-faint); }
.search-box input { flex: 1; border: 0; outline: 0; background: transparent; color: var(--color-text); font: inherit; }
.content { flex: 1; padding: 28px; overflow-y: auto; }
.account-link { display: block; padding: 6px; margin: -6px -6px 6px; border-radius: var(--radius-md); text-decoration: none; color: inherit; }
.account-link:hover, .account-link.active { background: var(--color-surface-muted); }
.logout { display: flex; align-items: center; gap: 8px; width: 100%; padding: 9px 10px; border: 1px solid var(--color-border); border-radius: var(--radius-md); background: transparent; color: var(--color-text-muted); font: inherit; font-size: 13px; cursor: pointer; }
.logout:hover { border-color: var(--ck-fail-500); color: var(--ck-fail-text); }
.sr-only { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0,0,0,0); }
@media (max-width: 700px) {
  .app-shell { flex-direction: column; }
  :deep(.ck-sidebar) { width: 100%; height: auto; position: static; flex-direction: row; align-items: center; }
  :deep(.ck-sidebar .nav) { display: flex; overflow-x: auto; }
  :deep(.ck-sidebar .footer) { display: none; }
  .topbar { flex-wrap: wrap; height: auto; padding: 14px 16px; }
  .search-box { max-width: none; order: 3; width: 100%; }
}
</style>
